import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button,
  CircularProgress, TextField, Grid, Select, MenuItem, FormControl, InputLabel, Drawer, Divider,
  Snackbar, Alert, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, IconButton,
  Accordion, AccordionSummary, AccordionDetails, RadioGroup, Radio, FormControlLabel, Switch, TablePagination, TableSortLabel,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import { API, getAuthHeader, fmtDay, fmtDate, numField, openAuthenticatedFile } from './customerApp/shared';
import FittedSignatureCanvas from '../components/RateAgreement/FittedSignatureCanvas';

// Company text boxes on a template are blanks the owner fills in when sending
const templateBlanks = (t) => (t?.fields || []).filter((f) => f.party === 'company' && f.type === 'text');

// The hiring site: no link back to wkroadss.com, so applicants never see service prices (backend/utils/careersSite.js)
const DEFAULT_SITE_URL = 'https://whiteknightroadsideservices.com';
const careersLink = (siteUrl, slug) => `${siteUrl}/careers.html#${slug}`;

const EMAIL_LABEL = {
  application_received: 'Application received (to applicant)',
  owner_new_applicant: 'New applicant alert (to you)',
  assessment_invite: 'Assessment invite (to applicant)',
  assessment_complete: 'Assessment complete + ask for times (to applicant)',
  owner_assessment_complete: 'Assessment finished alert (to you)',
  availability_request: 'Ask for interview times (to applicant)',
  owner_availability: 'Applicant gave their times (to you)',
  interview_scheduled: 'Interview proposed — confirm or change (to applicant)',
  interview_confirmed: 'Interview confirmed (to applicant)',
  owner_interview_confirmed: 'Applicant confirmed / moved the interview (to you)',
  rejected: 'Rejection (to applicant)',
  hired: 'Hired (to applicant)',
  documents_request: 'License & insurance request (to applicant)',
  contract_sent: 'Contract to sign (to applicant)',
  contract_reminder: 'Contract reminder (to applicant)',
  contract_signed: 'Signed copy (to applicant)',
  owner_documents: 'License & insurance uploaded (to you)',
  owner_contract_signed: 'Contract signed (to you)',
  owner_contract_countersign: 'Contract signed — needs your sign-off (to you)',
  contract_completed: 'Fully signed copy (to applicant)'
};
const emailLabel = (t) => EMAIL_LABEL[t] || String(t).replace(/_/g, ' ');
// Business time, not the viewer's device clock, so it matches schedules elsewhere in the portal
const fmtEmailTime = (d) => (d ? new Date(d).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : '—');
const fmtCentral = (d) => (d ? new Date(d).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const fmtCentralDay = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric' });
const fmtCentralTime = (d) => new Date(d).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hourLabel = (h) => (h === 0 || h === 24 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);
/** One line on where the phone interview stands, for the list and the drawer. */
const interviewSummary = (iv) => {
  if (!iv || iv.phase === 'assessment') return null;
  if (iv.phase === 'confirmed') return { text: `Confirmed ${fmtCentral(iv.scheduledFor)}`, color: 'success' };
  if (iv.phase === 'proposed') return { text: `Proposed ${fmtCentral(iv.scheduledFor)} — awaiting their confirmation`, color: 'warning' };
  if (iv.availabilityGivenAt) return { text: `Gave ${iv.availability.length} time${iv.availability.length === 1 ? '' : 's'} they can do — pick one`, color: 'info' };
  if (iv.availabilityRequestedAt) return { text: `Asked for their times ${fmtEmailTime(iv.availabilityRequestedAt)}`, color: 'default' };
  return { text: 'Not asked for interview times yet', color: 'default' };
};

const STATUS_LABEL = {
  applied: 'Applied', assessment_sent: 'Assessment sent', assessment_in_progress: 'Assessment started', assessment_complete: 'Assessment complete',
  interview_scheduled: 'Interview scheduled', hired: 'Hired', rejected: 'Not selected', withdrawn: 'Withdrawn'
};
const STATUS_COLOR = {
  applied: 'default', assessment_sent: 'info', assessment_in_progress: 'info', assessment_complete: 'warning',
  interview_scheduled: 'primary', hired: 'success', rejected: 'error', withdrawn: 'default',
  open: 'success', paused: 'warning', closed: 'default'
};
const StatusChip = ({ status }) => <Chip label={STATUS_LABEL[status] || status} size="small" color={STATUS_COLOR[status] || 'default'} />;
const APPLICANT_FILTERS = ['applied', 'assessment_sent', 'assessment_in_progress', 'assessment_complete', 'interview_scheduled', 'hired', 'rejected'];
// The applicants table is split three ways: still in the pipeline, hired, and not selected (rejected or withdrawn)
const SECTIONS = [['active', 'In the pipeline'], ['hired', 'Hired'], ['closed', 'Not selected']];
const sectionOf = (r) => (r.status === 'hired' ? 'hired' : ['rejected', 'withdrawn'].includes(r.status) ? 'closed' : 'active');
const SECTION_FILTERS = {
  active: APPLICANT_FILTERS.filter((s) => !['hired', 'rejected'].includes(s)),
  hired: [],
  closed: ['rejected', 'withdrawn']
};

const copy = (text, notify) => {
  navigator.clipboard?.writeText(text).then(() => notify('Copied', 'success')).catch(() => notify(text, 'info'));
};
const PAGE_SIZES = [10, 25, 50, 100];
const isPast = (d) => new Date(d).getTime() < Date.now();
/**
 * The times an applicant said they can do, as chips. Past ones are greyed; a click hands
 * the time back (to propose it or jump the scheduler there).
 */
function AvailabilityChips({ times, selected, onPick, size = 'small' }) {
  if (!times?.length) return null;
  const sel = selected ? new Date(selected).getTime() : null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {times.map((t) => {
        const past = isPast(t);
        const on = sel === new Date(t).getTime();
        const chip = <Chip size={size} label={`${fmtCentral(t)}${past ? ' · passed' : ''}`} color={on ? 'primary' : past ? 'default' : 'success'} variant={on ? 'filled' : 'outlined'} disabled={past} onClick={past || !onPick ? undefined : () => onPick(t)} />;
        return past ? <Tooltip key={t} title="This time has already passed"><span>{chip}</span></Tooltip> : <span key={t}>{chip}</span>;
      })}
    </Stack>
  );
}
const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
const fromLines = (s) => String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);

// ---- settings --------------------------------------------------------------------------

function SettingsCard({ settings, setSettings, onSave, saving }) {
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Pipeline settings</Typography>
        <Button variant="contained" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        An applicant gets a "we received your application" email right away, then the competency assessment link after the delay below.
        The assessment has four modules (tire change, jump start, lockout, fuel delivery); each needs a passing quiz{settings.videosRequired === true ? ' and a phone video' : ''}.
        When all four are done you get an email to schedule the phone interview.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={settings.videosRequired === true} onChange={(e) => set('videosRequired', e.target.checked)} />}
            label={settings.videosRequired === true ? 'Video required: ON (each module needs a phone video)' : 'Video required: OFF (quizzes only; the video step is hidden)'}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...numField({ label: 'Assessment email delay' })} value={settings.assessmentDelayMinutes ?? ''} onChange={(e) => set('assessmentDelayMinutes', e.target.value)} helperText="minutes after applying" inputProps={{ min: 0, max: 10080 }} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...numField({ label: 'Quiz pass mark' })} value={settings.passScorePercent ?? ''} onChange={(e) => set('passScorePercent', e.target.value)} helperText="percent" inputProps={{ min: 1, max: 100 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label="Notify email" value={settings.notifyEmail || ''} onChange={(e) => set('notifyEmail', e.target.value)} helperText="new applicants, completed assessments, interview times" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Phone-interview calendar (Central time)</Typography>
          <Typography variant="caption" color="text.secondary">Applicants pick from these slots when they give their times, confirm a proposed time or move it. A slot another applicant already holds is never offered.</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Days</InputLabel>
            <Select multiple label="Days" value={settings.interviewDays || []} onChange={(e) => set('interviewDays', e.target.value)} renderValue={(v) => v.slice().sort().map((d) => DAY_NAMES[d].slice(0, 3)).join(', ')}>
              {DAY_NAMES.map((n, i) => <MenuItem key={n} value={i}>{n}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>From</InputLabel>
            <Select label="From" value={settings.interviewStartHour ?? 10} onChange={(e) => set('interviewStartHour', e.target.value)}>
              {Array.from({ length: 24 }, (_, h) => <MenuItem key={h} value={h}>{hourLabel(h)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>To</InputLabel>
            <Select label="To" value={settings.interviewEndHour ?? 20} onChange={(e) => set('interviewEndHour', e.target.value)}>
              {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => <MenuItem key={h} value={h}>{hourLabel(h)}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Slot</InputLabel>
            <Select label="Slot" value={settings.interviewSlotMinutes ?? 30} onChange={(e) => set('interviewSlotMinutes', e.target.value)}>
              {[15, 20, 30, 45, 60].map((m) => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField {...numField({ label: 'Weeks ahead' })} value={settings.interviewWeeksAhead ?? ''} onChange={(e) => set('interviewWeeksAhead', e.target.value)} inputProps={{ min: 1, max: 8 }} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth multiline minRows={2} size="small" label="Extra paragraph for the interview email (optional)" value={settings.interviewEmailTemplate || ''} onChange={(e) => set('interviewEmailTemplate', e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Interview reminders on your phone</Typography>
          <Typography variant="caption" color="text.secondary">
            The driver app pings every owner before each phone interview: the first reminder this early, then again at the repeat interval until the duration runs out or you tap <b>Got it</b> on the notification. <b>Snooze</b> on the notification waits the snooze time before the next one. A moved interview starts its reminders over.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={settings.interviewRemindersEnabled !== false} onChange={(e) => set('interviewRemindersEnabled', e.target.checked)} />}
            label={settings.interviewRemindersEnabled !== false ? 'Phone reminders: ON' : 'Phone reminders: OFF'}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...numField({ label: 'First reminder' })} value={settings.interviewReminderLeadMinutes ?? ''} onChange={(e) => set('interviewReminderLeadMinutes', e.target.value)} helperText="minutes before the interview" inputProps={{ min: 5, max: 1440 }} disabled={settings.interviewRemindersEnabled === false} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...numField({ label: 'Repeat every' })} value={settings.interviewReminderRepeatMinutes ?? ''} onChange={(e) => set('interviewReminderRepeatMinutes', e.target.value)} helperText="minutes between reminders" inputProps={{ min: 1, max: 240 }} disabled={settings.interviewRemindersEnabled === false} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...numField({ label: 'Snooze' })} value={settings.interviewReminderSnoozeMinutes ?? ''} onChange={(e) => set('interviewReminderSnoozeMinutes', e.target.value)} helperText="minutes when you tap Snooze" inputProps={{ min: 1, max: 240 }} disabled={settings.interviewRemindersEnabled === false} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField {...numField({ label: 'Keep reminding for' })} value={settings.interviewReminderDurationMinutes ?? ''} onChange={(e) => set('interviewReminderDurationMinutes', e.target.value)} helperText="minutes after the first reminder" inputProps={{ min: 5, max: 1440 }} disabled={settings.interviewRemindersEnabled === false} />
        </Grid>
      </Grid>
    </Paper>
  );
}

// ---- reviewer link ---------------------------------------------------------------------

function ReviewerCard({ notify }) {
  const [link, setLink] = useState({ enabled: false, url: '' });
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [l, n] = await Promise.all([
        axios.get(`${API}/api/careers-admin/review-link`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/careers-admin/review-notes`, { headers: getAuthHeader() })
      ]);
      setLink(l.data);
      setNotes(n.data.notes || []);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not load the reviewer link', 'error');
    }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (link.enabled && !window.confirm('Make a new link? The current link will stop working.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/careers-admin/review-link`, {}, { headers: getAuthHeader() });
      setLink(data);
      notify(link.enabled ? 'New link created; the old one no longer works' : 'Reviewer link created', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Could not create the link', 'error');
    } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/api/careers-admin/review-link`, { headers: getAuthHeader() });
      setLink(data);
      notify('Reviewer link turned off', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Could not turn the link off', 'error');
    } finally { setBusy(false); }
  };
  const resolve = async (id, resolved) => {
    try {
      const { data } = await axios.post(`${API}/api/careers-admin/review-notes/${id}/resolve`, { resolved }, { headers: getAuthHeader() });
      setNotes(data.notes || []);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not update the note', 'error');
    }
  };

  const open = notes.filter((n) => !n.resolved).length;
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6">Reviewer link</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send this link to someone to read every assessment module, see the quiz answers and flag mistakes. Nothing they do is recorded as an application. Anyone with the link can view it until you turn it off or make a new one.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField size="small" fullWidth value={link.enabled ? link.url : ''} placeholder="No active link" InputProps={{ readOnly: true }} />
        <Button variant="contained" onClick={create} disabled={busy} sx={{ whiteSpace: 'nowrap' }}>{link.enabled ? 'New link' : 'Create link'}</Button>
        {link.enabled && <Button startIcon={<ContentCopyIcon />} onClick={() => copy(link.url, notify)} sx={{ whiteSpace: 'nowrap' }}>Copy</Button>}
        {link.enabled && <Button color="error" onClick={disable} disabled={busy} sx={{ whiteSpace: 'nowrap' }}>Turn off</Button>}
      </Stack>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Reviewer notes{notes.length ? ` (${open} open of ${notes.length})` : ''}</Typography>
      {notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Where</TableCell><TableCell>Note</TableCell><TableCell>From</TableCell><TableCell>Date</TableCell><TableCell align="right" /></TableRow>
          </TableHead>
          <TableBody>
            {notes.map((n) => (
              <TableRow key={n.id} sx={n.resolved ? { opacity: 0.55 } : undefined}>
                <TableCell>{[n.moduleKey, n.slideOrQuestion].filter(Boolean).join(' · ') || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>{n.text}</TableCell>
                <TableCell>{n.reviewerName || '—'}</TableCell>
                <TableCell>{fmtDate(n.at)}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => resolve(n.id, !n.resolved)}>{n.resolved ? 'Reopen' : 'Resolve'}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

// ---- postings --------------------------------------------------------------------------

const EMPTY_POSTING = { title: '', slug: '', location: 'Shreveport / Bossier City, LA', hiringCities: '', payRange: '', employmentType: 'Contract', summary: '', description: '', requirements: '', toolsRequired: '', schedule: '', supplementalPay: '', status: 'open', openings: 2 };

function PostingDialog({ open, initial, onClose, onSaved, notify }) {
  const [form, setForm] = useState(EMPTY_POSTING);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...initial,
      slug: initial.slug || '',
      requirements: toLines(initial.requirements), toolsRequired: toLines(initial.toolsRequired),
      schedule: toLines(initial.schedule), supplementalPay: toLines(initial.supplementalPay),
      hiringCities: toLines(initial.hiringCities)
    } : EMPTY_POSTING);
  }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.title.trim()) return notify('Title is required', 'error');
    setBusy(true);
    try {
      const body = {
        ...form,
        requirements: fromLines(form.requirements), toolsRequired: fromLines(form.toolsRequired),
        schedule: fromLines(form.schedule), supplementalPay: fromLines(form.supplementalPay),
        hiringCities: fromLines(form.hiringCities), openings: Number(form.openings)
      };
      if (initial?.id) await axios.put(`${API}/api/careers-admin/jobs/${initial.id}`, body, { headers: getAuthHeader() });
      else await axios.post(`${API}/api/careers-admin/jobs`, body, { headers: getAuthHeader() });
      notify('Posting saved', 'success');
      onSaved();
      onClose();
    } catch (err) {
      notify(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{initial?.id ? 'Edit posting' : (initial ? 'Duplicate posting' : 'New posting')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}><TextField size="small" fullWidth label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} /></Grid>
          <Grid item xs={6} sm={2}>
            <FormControl size="small" fullWidth><InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}>{['open', 'paused', 'closed'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}><TextField {...numField({ label: 'Openings' })} value={form.openings} onChange={(e) => set('openings', e.target.value)} inputProps={{ min: 0 }} /></Grid>
          <Grid item xs={12} sm={5}><TextField size="small" fullWidth label="City / location" value={form.location} onChange={(e) => set('location', e.target.value)} helperText="One posting per city; readers search by this" /></Grid>
          <Grid item xs={12} sm={7}><TextField size="small" fullWidth label="URL slug (optional)" value={form.slug} onChange={(e) => set('slug', e.target.value)} helperText="e.g. roadside-service-technician-dallas-tx — left blank it is made from the title" /></Grid>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Pay range" value={form.payRange} onChange={(e) => set('payRange', e.target.value)} placeholder="$600 – $1,200 per week" /></Grid>
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth><InputLabel>Type</InputLabel>
              <Select label="Type" value={form.employmentType} onChange={(e) => set('employmentType', e.target.value)}>{['Contract', 'Part-time', 'Full-time'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}><TextField size="small" fullWidth multiline minRows={2} label="Summary (one short paragraph shown in the list)" value={form.summary} onChange={(e) => set('summary', e.target.value)} /></Grid>
          <Grid item xs={12}><TextField size="small" fullWidth multiline minRows={8} label="Description" helperText='Blank line between paragraphs; start a line with "- " for a bullet' value={form.description} onChange={(e) => set('description', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth multiline minRows={4} label="Requirements (one per line)" value={form.requirements} onChange={(e) => set('requirements', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth multiline minRows={4} label="Tools required (one per line)" value={form.toolsRequired} onChange={(e) => set('toolsRequired', e.target.value)} helperText="Applicants tick the ones they own" /></Grid>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth multiline minRows={4} label="Hiring cities (one per line)" value={form.hiringCities} onChange={(e) => set('hiringCities', e.target.value)} helperText="Applicants pick the area they are applying for; leave empty for a single-area posting" /></Grid>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth multiline minRows={3} label="Schedule (one per line)" value={form.schedule} onChange={(e) => set('schedule', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth multiline minRows={3} label="Supplemental pay (one per line)" value={form.supplementalPay} onChange={(e) => set('supplementalPay', e.target.value)} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function PostingsCard({ jobs, onChanged, notify, siteUrl = DEFAULT_SITE_URL }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const locations = useMemo(() => [...new Set(jobs.map((j) => j.location).filter(Boolean))].sort(), [jobs]);
  const filtered = location ? jobs.filter((j) => j.location === location) : jobs;
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / perPage) - 1));
  const shown = filtered.slice(safePage * perPage, safePage * perPage + perPage);
  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? It is closed instead if anyone has applied.`)) return;
    try {
      const { data } = await axios.delete(`${API}/api/careers-admin/jobs/${job.id}`, { headers: getAuthHeader() });
      notify(data.message || 'Posting deleted', 'success');
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Delete failed', 'error');
    }
  };
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Job postings</Typography>
        <Button variant="contained" onClick={() => { setEditing(null); setOpen(true); }}>Add posting</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open postings appear at {siteUrl}/ — the hiring site, which has no link back to wkroadss.com. Point your Indeed listing at the careers link so applicants come here instead of applying on Indeed.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Location</InputLabel>
          <Select label="Location" value={location} onChange={(e) => { setLocation(e.target.value); setPage(0); }}>
            <MenuItem value="">All locations</MenuItem>
            {locations.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">{location ? `${filtered.length} of ${jobs.length}` : jobs.length} posting{jobs.length === 1 ? '' : 's'}</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow><TableCell>Title</TableCell><TableCell>City</TableCell><TableCell>Pay</TableCell><TableCell>Status</TableCell><TableCell>Openings</TableCell><TableCell>Applicants</TableCell><TableCell>Posted</TableCell><TableCell /></TableRow>
        </TableHead>
        <TableBody>
          {jobs.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No postings yet</Typography></TableCell></TableRow>}
          {jobs.length > 0 && filtered.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No postings in {location}</Typography></TableCell></TableRow>}
          {shown.map((j) => (
            <TableRow key={j.id} hover>
              <TableCell>{j.title}<Typography variant="caption" display="block" color="text.secondary">{j.employmentType} · /{j.slug}</Typography></TableCell>
              <TableCell>{j.location}</TableCell>
              <TableCell>{j.payRange || '—'}</TableCell>
              <TableCell><StatusChip status={j.status} /></TableCell>
              <TableCell>{j.openings}</TableCell>
              <TableCell>{j.applicants ?? 0}</TableCell>
              <TableCell>{fmtDay(j.postedAt)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title={careersLink(siteUrl, j.slug)}><Button size="small" onClick={() => copy(careersLink(siteUrl, j.slug), notify)}>Copy careers link</Button></Tooltip>
                <Tooltip title="Same link — paste it as the apply URL on Indeed"><Button size="small" onClick={() => copy(careersLink(siteUrl, j.slug), notify)}>Copy Indeed apply URL</Button></Tooltip>
                <Tooltip title="Duplicate for another city or position"><IconButton size="small" onClick={() => { setEditing({ ...j, id: undefined, slug: '', title: `${j.title} (copy)`, status: 'open' }); setOpen(true); }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                <IconButton size="small" onClick={() => { setEditing(j); setOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => remove(j)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {filtered.length > PAGE_SIZES[0] && (
        <TablePagination component="div" count={filtered.length} page={safePage} rowsPerPage={perPage} rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }} />
      )}
      <PostingDialog open={open} initial={editing} onClose={() => setOpen(false)} onSaved={onChanged} notify={notify} />
    </Paper>
  );
}

// ---- applicant detail ---------------------------------------------------------------------

const YesNo = ({ v }) => <span style={{ color: v ? '#2e7d32' : '#c62828', fontWeight: 700 }}>{v ? '✓' : '✗'}</span>;

const PLEA_LABEL = { guilty: 'Guilty', not_guilty: 'Not guilty', no_contest: 'No contest', other: 'Other' };
const Answer = ({ v }) => <b style={{ color: v ? '#c62828' : '#2e7d32' }}>{v ? 'Yes' : 'No'}</b>;

/** Background and driving history as the applicant answered it. */
const HistorySection = ({ h }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>Background &amp; driving history</Typography>
    <Typography variant="body2">Criminal record: <Answer v={h.hasCriminalRecord} /></Typography>
    {h.hasCriminalRecord && (h.criminalRecords || []).map((r, i) => (
      <Box key={i} sx={{ ml: 2, mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
        <Typography variant="body2"><b>Charge:</b> {r.charge}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}><b>Situation:</b> {r.situation}</Typography>
        <Typography variant="body2"><b>Plea:</b> {r.pleaMade ? (r.plea === 'other' ? `Other — ${r.pleaOther}` : PLEA_LABEL[r.plea] || '—') : 'No plea entered'}</Typography>
      </Box>
    ))}
    <Typography variant="body2" sx={{ mt: 0.5 }}>Tickets / moving violations (3 yrs): <Answer v={h.hasTickets3y} /></Typography>
    {h.hasTickets3y && <Typography variant="body2" sx={{ ml: 2, whiteSpace: 'pre-wrap' }}>{h.ticketsDetail}</Typography>}
    <Typography variant="body2" sx={{ mt: 0.5 }}>Accidents (3 yrs): <Answer v={h.hasAccidents3y} /></Typography>
    {h.hasAccidents3y && <Typography variant="body2" sx={{ ml: 2, whiteSpace: 'pre-wrap' }}>{h.accidentsDetail}</Typography>}
    <Typography variant="body2" sx={{ mt: 0.5 }}>License points: <Answer v={h.hasLicensePoints} />{h.hasLicensePoints && h.licensePoints != null ? ` — ${h.licensePoints}` : ''}</Typography>
    {h.hasLicensePoints && <Typography variant="body2" sx={{ ml: 2, whiteSpace: 'pre-wrap' }}>{h.licensePointsDetail}</Typography>}
  </Box>
);

/** One uploaded clip; loaded on demand because clips can be hundreds of MB. */
function ClipPlayer({ applicantId, moduleKey, clip, index, count }) {
  const [src, setSrc] = useState(null);
  const load = async () => {
    try {
      const res = await fetch(`${API}/api/careers-admin/applicants/${applicantId}/video/${moduleKey}/${encodeURIComponent(clip.id)}`, { headers: getAuthHeader() });
      if (!res.ok) throw new Error('video');
      setSrc(URL.createObjectURL(await res.blob()));
    } catch { setSrc('error'); }
  };
  useEffect(() => () => { if (src && src !== 'error') URL.revokeObjectURL(src); }, [src]);
  const label = count > 1 ? `Clip ${index + 1} of ${count}` : 'Video';
  return (
    <Box sx={{ mb: 1.5 }}>
      {!src && <Button size="small" variant="outlined" onClick={load}>Load {label.toLowerCase()} ({Math.round((clip.size || 0) / 1048576)} MB)</Button>}
      {src === 'error' && <Typography variant="body2" color="error">Could not load {label.toLowerCase()}</Typography>}
      {src && src !== 'error' && <video controls src={src} style={{ width: '100%', maxHeight: 360, background: '#000', borderRadius: 8 }} />}
      <Typography variant="caption" color="text.secondary" display="block">{label} · {clip.originalName} · {fmtDate(clip.uploadedAt)}</Typography>
    </Box>
  );
}

function ModulePanel({ applicantId, m }) {
  const clips = Array.isArray(m.videos) ? m.videos : (m.video ? [{ id: 'legacy', ...m.video }] : []);
  const pct = m.total ? Math.round((m.bestScore / m.total) * 100) : 0;
  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.title}</Typography>
          <Chip size="small" label={`${m.bestScore}/${m.total} (${pct}%)`} color={m.passed ? 'success' : 'default'} />
          <Chip size="small" variant="outlined" label={`${Array.isArray(m.attempts) ? m.attempts.length : m.attempts} attempt(s)`} />
          <Chip size="small" variant="outlined" color={clips.length ? 'success' : 'warning'} label={clips.length ? (clips.length === 1 ? 'video uploaded' : `${clips.length} clips uploaded`) : 'no video'} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {clips.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {clips.length > 1 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>The applicant split this job across {clips.length} clips; watch them in order.</Typography>}
            {clips.map((c, i) => <ClipPlayer key={c.id} applicantId={applicantId} moduleKey={m.key} clip={c} index={i} count={clips.length} />)}
          </Box>
        )}
        {Array.isArray(m.attempts) && m.attempts.map((att, i) => (
          <Box key={i} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Attempt {i + 1} · {fmtDate(att.at)} · {att.score}/{att.total} {att.passed ? '· passed' : ''}</Typography>
            <Table size="small">
              <TableBody>
                {att.answers.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell sx={{ width: '55%' }}>{a.question}</TableCell>
                    <TableCell sx={{ color: a.chosen === a.correct ? 'success.main' : 'error.main' }}>{a.chosenText ?? '— (no answer)'}</TableCell>
                    <TableCell>{a.chosen === a.correct ? '' : <Typography variant="caption" color="text.secondary">correct: {a.correctText}</Typography>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
        {Array.isArray(m.attempts) && m.attempts.length === 0 && <Typography variant="body2" color="text.secondary">No quiz attempts yet</Typography>}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * The interview calendar for one applicant: a day picker and that day's slots.
 * Their preferred times are green, taken slots are greyed with who holds them.
 */
function SlotPicker({ applicantId, value, onChange, notify }) {
  const [cal, setCal] = useState(null);
  const [day, setDay] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/careers-admin/applicants/${applicantId}/interview`, { headers: getAuthHeader() });
        if (!alive) return;
        setCal(data);
        const current = value ? data.slots.find((s) => new Date(s.start).getTime() === new Date(value).getTime()) : null;
        const first = current || data.slots.find((s) => s.preferred && !s.taken) || data.slots.find((s) => !s.taken);
        if (first) { setDay(fmtCentralDay(first.start)); if (!current) onChange(first.start); }
      } catch (err) {
        notify(err.response?.data?.message || 'Failed to load the interview calendar', 'error');
      }
    })();
    return () => { alive = false; };
    // Loads once per applicant; the selection is seeded from the first open preferred slot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);
  if (!cal) return <CircularProgress size={20} />;
  const days = [];
  for (const s of cal.slots) {
    const k = fmtCentralDay(s.start);
    let d = days.find((x) => x.key === k);
    if (!d) { d = { key: k, slots: [], free: 0, preferred: 0 }; days.push(d); }
    d.slots.push(s);
    if (!s.taken) d.free++;
    if (s.preferred) d.preferred++;
  }
  const shown = days.find((d) => d.key === day) || days[0];
  const selected = value ? new Date(value).getTime() : null;
  const theirs = cal.availability || [];
  return (
    <Box>
      {theirs.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Times they said they can do — tap one to use it</Typography>
          <AvailabilityChips times={theirs} selected={value} onPick={(t) => { setDay(fmtCentralDay(t)); onChange(t); }} />
          <Typography variant="caption" color="text.secondary">Or pick any other open slot below.</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Day</InputLabel>
          <Select label="Day" value={shown?.key || ''} onChange={(e) => setDay(e.target.value)}>
            {days.map((d) => <MenuItem key={d.key} value={d.key}>{d.key} · {d.free} open{d.preferred ? ` · ${d.preferred} they can do` : ''}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">Central time · green = times they said they can do · grey = another applicant's interview</Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {(shown?.slots || []).map((s) => {
          const t = new Date(s.start).getTime();
          const chip = (
            <Chip
              key={s.start}
              label={fmtCentralTime(s.start)}
              size="small"
              disabled={s.taken}
              color={selected === t ? 'primary' : s.preferred ? 'success' : 'default'}
              variant={selected === t ? 'filled' : 'outlined'}
              onClick={s.taken ? undefined : () => onChange(s.start)}
            />
          );
          return s.taken ? <Tooltip key={s.start} title={`${s.takenBy || 'Another applicant'} has an interview then`}><span>{chip}</span></Tooltip> : chip;
        })}
        {shown && shown.slots.length === 0 && <Typography variant="body2" color="text.secondary">No slots that day</Typography>}
      </Stack>
      {days.length === 0 && <Typography variant="body2" color="text.secondary">No open slots in the next {cal.window?.weeksAhead || 2} weeks — widen the calendar in Pipeline settings.</Typography>}
    </Box>
  );
}

/**
 * "Mark hired": the owner picks the contract (a template, a one-off file, or none) and
 * the hire gets ONE welcome email linking the onboarding page, where they upload their
 * license and insurance and sign.
 */
function HireDialog({ applicant, onClose, onHired, notify }) {
  const [templates, setTemplates] = useState(null);
  const [wordSupported, setWordSupported] = useState(false);
  const [mode, setMode] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState({});
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/careers-admin/contract-templates`, { headers: getAuthHeader() })
      .then(({ data }) => { setTemplates(data.templates || []); setWordSupported(!!data.wordSupported); if ((data.templates || []).length) setMode('template'); })
      .catch(() => setTemplates([]));
  }, []);
  const t = (templates || []).find((x) => x.id === templateId) || null;
  const blanks = templateBlanks(t);
  const missingBlank = blanks.some((f) => f.required !== false && !(values[f.id] || '').trim());
  const ready = mode === 'none' || (mode === 'template' && t && !missingBlank) || (mode === 'upload' && file);

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const form = new FormData();
      if (note.trim()) form.append('note', note.trim());
      if (mode === 'template') { form.append('templateId', templateId); form.append('ownerValues', JSON.stringify(values)); }
      if (mode === 'upload') { form.append('title', title); form.append('file', file); }
      await axios.post(`${API}/api/careers-admin/applicants/${applicant.id || applicant._id}/hire`, form, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      notify(mode === 'none' ? 'Hired — welcome email sent with the license and insurance link' : 'Hired — welcome email sent with the license and insurance link and the contract to sign', 'success');
      onHired();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to hire', 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Hire {applicant.name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          They get one welcome email with a link to their onboarding page. There they upload their driver's license (front and back) and proof of insurance, and sign the contract you pick here.
        </Typography>
        <Typography variant="subtitle2">Which contract should they sign?</Typography>
        {templates === null ? <CircularProgress size={24} sx={{ my: 1 }} /> : (
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="template" disabled={templates.length === 0} control={<Radio />} label={templates.length ? 'One of my contract templates' : 'One of my contract templates (none yet)'} />
            {mode === 'template' && (
              <Box sx={{ pl: 4, pb: 1 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select label="Template" value={templateId} onChange={(e) => { setTemplateId(e.target.value); setValues({}); }}>
                    {templates.map((x) => <MenuItem key={x.id} value={x.id}>{x.title} · {x.fields.length} field{x.fields.length === 1 ? '' : 's'}{templateBlanks(x).length ? ` · ${templateBlanks(x).length} blank${templateBlanks(x).length === 1 ? '' : 's'} to fill` : ''}</MenuItem>)}
                  </Select>
                </FormControl>
                {t && t.fields.length === 0 && <Alert severity="warning" sx={{ mt: 1 }}>This template has no fields placed yet, so they would sign on a certificate page at the end only.</Alert>}
                {blanks.length > 0 && (
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Fill in the blanks. They're printed into the contract exactly as typed.</Typography>
                    {blanks.map((f) => (
                      <TextField key={f.id} size="small" fullWidth label={`${f.label || 'Blank'} (page ${f.page})`} required={f.required !== false} value={values[f.id] || ''} inputProps={{ maxLength: 200 }}
                        onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))} />
                    ))}
                  </Stack>
                )}
              </Box>
            )}
            <FormControlLabel value="upload" control={<Radio />} label="Upload a contract just for them" />
            {mode === 'upload' && (
              <Box sx={{ pl: 4, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" label="Contract title" placeholder="e.g. Independent contractor agreement" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ minWidth: 240, flex: 1 }} />
                <Button size="small" variant="outlined" component="label">
                  {file ? file.name : 'Choose PDF or Word file'}
                  <input hidden type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '')); }} />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  They sign on a certificate page at the end. To put signature boxes exactly where you want them, or to reuse it for other hires, build it under Contract templates first. {wordSupported ? '' : 'Word files: save as PDF first, the server cannot convert them yet.'}
                </Typography>
              </Box>
            )}
            <FormControlLabel value="none" control={<Radio />} label="No contract for now (just the license and insurance pictures)" />
          </RadioGroup>
        )}
        <TextField size="small" fullWidth multiline minRows={2} label="Personal note in the email (optional)" placeholder="e.g. Your first shift is Monday at 8am." value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 2000 }} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="success" disabled={busy || !ready} onClick={submit}>{busy ? 'Sending...' : 'Hire and send welcome email'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ApplicantDrawer({ id, onClose, notify, onChanged }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [when, setWhen] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [contractTitle, setContractTitle] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  useEffect(() => {
    if (!id) return;
    axios.get(`${API}/api/careers-admin/contract-templates`, { headers: getAuthHeader() })
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => setTemplates([]));
  }, [id]);
  // Blanks to fill before a template goes out: { template, values } while the dialog is open
  const [blanks, setBlanks] = useState(null);
  const sendTemplate = async (ownerValues) => {
    if (!templateId) return;
    const t = templates.find((x) => x.id === templateId);
    if (t && t.fields.length === 0 && !window.confirm(`"${t.title}" has no fields placed yet — the hire would sign on a certificate page only. Send anyway?`)) return;
    if (!ownerValues && templateBlanks(t).length) { setBlanks({ template: t, values: {} }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/careers-admin/applicants/${id}/contracts/from-template`, { templateId, ownerValues: ownerValues || {} }, { headers: getAuthHeader() });
      notify('Contract sent — they got an email with the signing link', 'success');
      setTemplateId('');
      setBlanks(null);
      await load();
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to send the contract', 'error');
    } finally {
      setBusy(false);
    }
  };
  // The owner's sign-off after the hire signed: name + drawn signature
  const [signOff, setSignOff] = useState(null);
  const [signOffName, setSignOffName] = useState('');
  const signOffPad = useRef(null);
  const countersign = async () => {
    if (!signOff) return;
    if (!signOffName.trim()) { notify('Type your name as it should appear on the contract', 'error'); return; }
    if (!signOffPad.current || signOffPad.current.isEmpty()) { notify('Draw your signature first', 'error'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/careers-admin/applicants/${id}/contracts/${signOff.id}/countersign`, { signerName: signOffName.trim(), signatureData: signOffPad.current.toDataURL('image/png') }, { headers: getAuthHeader() });
      notify('Signed off — they got the fully signed copy by email', 'success');
      setSignOff(null);
      await load();
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to sign off', 'error');
    } finally {
      setBusy(false);
    }
  };
  const [contractFile, setContractFile] = useState(null);
  const contractInput = useRef(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/api/careers-admin/applicants/${id}`, { headers: getAuthHeader() });
      setA(data.applicant);
      setWhen(data.applicant.interview?.scheduledFor || '');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load applicant', 'error');
    }
  }, [id, notify]);
  useEffect(() => { setA(null); setScheduling(false); setRejecting(false); load(); }, [load]);

  const post = async (path, body, success) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/careers-admin/applicants/${id}/${path}`, body, { headers: getAuthHeader() });
      notify(success, 'success');
      await load();
      onChanged();
      return true;
    } catch (err) {
      notify(err.response?.data?.message || 'Action failed', 'error');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const canSchedule = !!a && !['hired', 'rejected', 'withdrawn'].includes(a.status);
  const addNote = async () => { if (!note.trim()) return; if (await post('notes', { text: note }, 'Note added')) setNote(''); };
  const schedule = async () => {
    if (!when) return;
    const ok = await post('status', { status: 'interview_scheduled', scheduledFor: new Date(when).toISOString() }, 'Time proposed — applicant emailed to confirm or pick another');
    if (ok) setScheduling(false);
  };
  const reject = async () => { if (await post('status', { status: 'rejected', note: reason }, 'Marked not selected — applicant emailed')) { setRejecting(false); setReason(''); } };
  const reopen = () => { if (window.confirm(`Reverse the decision on ${a.name}? They go back into the pipeline and are NOT emailed.`)) post('reopen', {}, 'Rejection reversed, applicant is back in the pipeline'); };
  const unhire = () => {
    const open = (a.onboarding?.contracts || []).filter((c) => c.status === 'sent').length;
    const warn = open ? ` Their ${open === 1 ? 'unsigned contract is' : `${open} unsigned contracts are`} withdrawn and the signing link stops working.` : '';
    if (window.confirm(`Withdraw the hire for ${a.name}?${warn} They go back into the pipeline and are NOT emailed — you can then mark them not selected.`)) post('unhire', {}, 'Hire withdrawn — they are back in the pipeline');
  };
  const sendContract = async () => {
    if (!contractFile) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('title', contractTitle);
      form.append('file', contractFile);
      await axios.post(`${API}/api/careers-admin/applicants/${id}/contracts`, form, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      notify('Contract sent — they got an email with the signing link', 'success');
      setContractTitle(''); setContractFile(null); if (contractInput.current) contractInput.current.value = '';
      await load();
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to send the contract', 'error');
    } finally {
      setBusy(false);
    }
  };
  const withdrawContract = async (c) => {
    if (!window.confirm(`Withdraw "${c.title}"? They will no longer see it.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/careers-admin/applicants/${id}/contracts/${c.id}`, { headers: getAuthHeader() });
      notify('Contract withdrawn', 'success');
      await load();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to withdraw', 'error');
    } finally {
      setBusy(false);
    }
  };
  const openFile = (url) => openAuthenticatedFile(url).catch((e) => notify(e.message, 'error'));
  const [hiring, setHiring] = useState(false);
  const hire = () => setHiring(true);

  const toolsRequired = a?.toolsRequired || [];
  const owned = useMemo(() => new Set((a?.toolsOwned || []).map((t) => t.toLowerCase())), [a]);

  return (
    <Drawer anchor="right" open={!!id} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 640 }, p: 3 } }}>
      {!a ? <CircularProgress /> : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{a.name}</Typography>
            <StatusChip status={a.status} />
          </Box>
          <Typography variant="body2" color="text.secondary">{a.jobTitle || a.jobSlug} · applied {fmtDate(a.appliedAt)}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}><b>Phone:</b> <a href={`tel:${a.phone}`}>{a.phone}</a> · <b>Email:</b> <a href={`mailto:${a.email}`}>{a.email}</a></Typography>
          <Typography variant="body2"><b>Applying for:</b> {a.applyCity || '—'}</Typography>
          <Typography variant="body2"><b>Address:</b> {[a.address, [a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' · ') || '—'}{a.heardFrom ? ` · heard from: ${a.heardFrom}` : ''}</Typography>
          {a.interview?.scheduledFor && <Typography variant="body2"><b>Interview:</b> {fmtDate(a.interview.scheduledFor)}</Typography>}

          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1}>
            <Grid item xs={6}><Typography variant="body2"><YesNo v={a.hasVehicle} /> Vehicle{a.vehicleDetails ? ` — ${a.vehicleDetails}` : ''}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2"><YesNo v={a.hasSmartphone} /> Smartphone</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2"><YesNo v={a.hasLicense} /> Driver's license{a.licenseState ? ` (${a.licenseState})` : ''}</Typography></Grid>
            {a.hasInsurance != null && <Grid item xs={6}><Typography variant="body2"><YesNo v={a.hasInsurance} /> Auto insurance</Typography></Grid>}
            {a.canWorkWeather != null && <Grid item xs={6}><Typography variant="body2"><YesNo v={a.canWorkWeather} /> Works in harsh weather</Typography></Grid>}
            {a.canLift50 != null && <Grid item xs={6}><Typography variant="body2"><YesNo v={a.canLift50} /> Lifts 50 lbs consistently</Typography></Grid>}
            {a.canWorkLongHours != null && <Grid item xs={6}><Typography variant="body2"><YesNo v={a.canWorkLongHours} /> Works long hours</Typography></Grid>}
            {a.history?.hasCriminalRecord == null && <Grid item xs={6}><Typography variant="body2"><YesNo v={a.cleanRecord} /> Clean record (self-reported)</Typography></Grid>}
          </Grid>
          {a.history?.hasCriminalRecord != null && <HistorySection h={a.history} />}
          {toolsRequired.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Tools</Typography>
              {toolsRequired.map((t) => <Typography key={t} variant="body2"><YesNo v={owned.has(t.toLowerCase())} /> {t}</Typography>)}
              {(a.toolsOwned || []).filter((t) => !toolsRequired.some((r) => r.toLowerCase() === t.toLowerCase())).map((t) => <Typography key={t} variant="body2"><YesNo v /> {t}</Typography>)}
            </Box>
          )}
          {(a.availability || []).length > 0 && <Typography variant="body2" sx={{ mt: 1 }}><b>Availability:</b> {a.availability.join(', ')}</Typography>}
          {a.experience && <><Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>Experience</Typography><Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{a.experience}</Typography></>}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {a.resume && <Button size="small" variant="outlined" onClick={() => openAuthenticatedFile(`${API}/api/careers-admin/applicants/${id}/resume`).catch((e) => notify(e.message, 'error'))}>Open resume ({a.resume.originalName})</Button>}
            {a.assessmentLink && <Button size="small" onClick={() => copy(a.assessmentLink, notify)}>Copy assessment link</Button>}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Competency assessment
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {a.assessment.completedAt ? `completed ${fmtDate(a.assessment.completedAt)}` : a.assessment.startedAt ? `started ${fmtDate(a.assessment.startedAt)}` : a.assessment.emailSentAt ? `invite sent ${fmtDate(a.assessment.emailSentAt)}` : `invite due ${fmtDate(a.assessment.emailDueAt)}`}
            </Typography>
          </Typography>
          {(a.modules || []).map((m) => <ModulePanel key={m.key} applicantId={id} m={m} />)}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Phone interview</Typography>
          {(() => {
            const iv = a.interview || {};
            const s = interviewSummary(iv);
            if (!s) return <Typography variant="body2" color="text.secondary">Opens once they finish the Skills Assessment; they're asked for their times in the completion email.</Typography>;
            return (
              <>
                <Chip size="small" color={s.color} label={s.text} sx={{ mb: 1 }} />
                {iv.availability?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Times they can do (Central){iv.availabilityGivenAt ? ` · given ${fmtEmailTime(iv.availabilityGivenAt)}` : ''}</Typography>
                    <AvailabilityChips times={iv.availability} selected={scheduling ? when : null} onPick={canSchedule ? (t) => { setWhen(t); setScheduling(true); } : null} />
                    {canSchedule && <Typography variant="caption" color="text.secondary">Tap a time to propose it, or use "Schedule interview" below for a different one.</Typography>}
                  </Box>
                )}
                {iv.scheduledFor && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Proposed {fmtEmailTime(iv.proposedAt)}{iv.proposedBy ? ` by ${iv.proposedBy}` : ''}{iv.confirmedAt ? ` · confirmed by the ${iv.confirmedVia || 'applicant'} ${fmtEmailTime(iv.confirmedAt)}` : ' · they have not confirmed yet'}
                  </Typography>
                )}
              </>
            );
          })()}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Onboarding (after hire)</Typography>
          {a.status !== 'hired' ? (
            <Typography variant="body2" color="text.secondary">"Mark hired" asks which contract to send, then emails them one link to upload their license and insurance and sign it. Anything else can be sent from here afterwards.</Typography>
          ) : (() => {
            const ob = a.onboarding || { documents: {}, contracts: [] };
            const docs = Object.entries(ob.documents || {});
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>License & insurance</Typography>
                  <Button size="small" variant={ob.documentsRequestedAt ? 'text' : 'outlined'} disabled={busy} onClick={() => post('request-documents', {}, 'Emailed them to upload their license and insurance')}>{ob.documentsRequestedAt ? 'Ask again' : 'Request pictures by email'}</Button>
                  {ob.documentsRequestedAt && <Typography variant="caption" color="text.secondary">asked {fmtEmailTime(ob.documentsRequestedAt)}{ob.documentsSubmittedAt ? ` · all in ${fmtEmailTime(ob.documentsSubmittedAt)}` : ''}</Typography>}
                </Box>
                {docs.map(([kind, d]) => (
                  <Box key={kind} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ minWidth: 190 }}><YesNo v={!!d.uploaded} /> {d.label}</Typography>
                    {d.uploaded ? <Button size="small" onClick={() => openFile(`${API}/api/careers-admin/applicants/${id}/document/${kind}`)}>Open ({fmtEmailTime(d.uploadedAt)})</Button> : <Typography variant="caption" color="text.secondary">not uploaded</Typography>}
                  </Box>
                ))}

                <Typography variant="body2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>Contracts to sign</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>Send a template</InputLabel>
                    <Select label="Send a template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                      {templates.length === 0 && <MenuItem value="" disabled>No templates yet — add one under Hiring → Contract templates</MenuItem>}
                      {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.title} · {t.fields.length} field{t.fields.length === 1 ? '' : 's'}{templateBlanks(t).length ? ` · ${templateBlanks(t).length} blank${templateBlanks(t).length === 1 ? '' : 's'} to fill` : ''}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button size="small" variant="contained" disabled={busy || !templateId} onClick={() => sendTemplate()}>Send for signature</Button>
                </Box>
                {(ob.contracts || []).length === 0 && <Typography variant="body2" color="text.secondary">None sent yet.</Typography>}
                {(ob.contracts || []).map((c) => (
                  <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5, opacity: c.status === 'voided' ? 0.6 : 1 }}>
                    <Chip size="small"
                      label={c.status === 'completed' ? 'Fully signed' : c.needsCountersign ? 'Needs your sign-off' : c.status === 'signed' ? 'Signed' : c.status === 'voided' ? 'Withdrawn' : 'Awaiting signature'}
                      color={c.status === 'completed' ? 'success' : c.needsCountersign ? 'info' : c.status === 'signed' ? 'success' : c.status === 'voided' ? 'default' : 'warning'} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.title}{c.hasFields ? <Typography component="span" variant="caption" color="text.secondary"> · {c.fields.length} field{c.fields.length === 1 ? '' : 's'}</Typography> : null}</Typography>
                    <Typography variant="caption" color="text.secondary">sent {fmtEmailTime(c.sentAt)}{c.remindedAt ? ` · reminded ${fmtEmailTime(c.remindedAt)}` : ''}{c.signedAt ? ` · signed ${fmtEmailTime(c.signedAt)} as "${c.signerName}"` : ''}{c.countersignedAt ? ` · signed off ${fmtEmailTime(c.countersignedAt)} as "${c.countersignerName}"` : ''}</Typography>
                    <Button size="small" onClick={() => openFile(`${API}/api/careers-admin/applicants/${id}/contracts/${c.id}/file`)}>Open</Button>
                    {c.status === 'signed' && <Button size="small" variant="outlined" color="success" onClick={() => openFile(`${API}/api/careers-admin/applicants/${id}/contracts/${c.id}/file?which=signed`)}>Open signed copy</Button>}
                    {c.status === 'completed' && <Button size="small" variant="outlined" color="success" onClick={() => openFile(`${API}/api/careers-admin/applicants/${id}/contracts/${c.id}/file?which=completed`)}>Open fully signed copy</Button>}
                    {c.needsCountersign && <Button size="small" variant="contained" color="info" disabled={busy} onClick={() => { setSignOffName(''); setSignOff(c); }}>Sign off</Button>}
                    {c.status === 'sent' && <Button size="small" disabled={busy} onClick={() => post(`contracts/${c.id}/remind`, {}, 'Reminder sent')}>Remind</Button>}
                    {c.status === 'sent' && <Button size="small" color="error" disabled={busy} onClick={() => withdrawContract(c)}>Withdraw</Button>}
                  </Box>
                ))}
                {blanks && (
                  <Dialog open onClose={() => setBlanks(null)} fullWidth maxWidth="sm">
                    <DialogTitle>Fill in the blanks — {blanks.template.title}</DialogTitle>
                    <DialogContent dividers>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>These are printed into the contract before it goes to {a.name}. They will see them exactly as typed here.</Typography>
                      <Stack spacing={2}>
                        {templateBlanks(blanks.template).map((f) => (
                          <TextField key={f.id} size="small" fullWidth label={`${f.label || 'Blank'} (page ${f.page})`} required={f.required !== false} value={blanks.values[f.id] || ''} inputProps={{ maxLength: 200 }}
                            onChange={(e) => setBlanks((b) => ({ ...b, values: { ...b.values, [f.id]: e.target.value } }))} />
                        ))}
                      </Stack>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setBlanks(null)} disabled={busy}>Cancel</Button>
                      <Button variant="contained" disabled={busy || templateBlanks(blanks.template).some((f) => f.required !== false && !(blanks.values[f.id] || '').trim())} onClick={() => sendTemplate(blanks.values)}>Send for signature</Button>
                    </DialogActions>
                  </Dialog>
                )}
                {signOff && (
                  <Dialog open onClose={busy ? undefined : () => setSignOff(null)} fullWidth maxWidth="sm">
                    <DialogTitle>Sign off — {signOff.title}</DialogTitle>
                    <DialogContent dividers>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{a.name} signed on {fmtEmailTime(signOff.signedAt)}. Your signature, printed name and today's date go into the company boxes, and they get the fully signed copy by email.</Typography>
                      <TextField size="small" fullWidth label="Your name, as it should print on the contract" value={signOffName} onChange={(e) => setSignOffName(e.target.value)} inputProps={{ maxLength: 120 }} sx={{ my: 1.5 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Draw your signature (finger or mouse):</Typography>
                      <Box sx={{ position: 'relative', width: '100%', height: 180, border: '1px dashed', borderColor: 'text.disabled', borderRadius: 1, bgcolor: '#fff', touchAction: 'none' }}>
                        <FittedSignatureCanvas ref={signOffPad} height={180} />
                      </Box>
                      <Button size="small" sx={{ mt: 0.5 }} onClick={() => signOffPad.current?.clear()}>Clear</Button>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setSignOff(null)} disabled={busy}>Cancel</Button>
                      <Button variant="contained" disabled={busy} onClick={countersign}>{busy ? 'Signing...' : 'Sign and send the final copy'}</Button>
                    </DialogActions>
                  </Dialog>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                  <TextField size="small" label="Contract title" placeholder="e.g. Independent contractor agreement" value={contractTitle} onChange={(e) => setContractTitle(e.target.value)} sx={{ minWidth: 260 }} />
                  <Button size="small" variant="outlined" component="label" disabled={busy}>
                    {contractFile ? contractFile.name : 'Choose PDF or Word file'}
                    <input ref={contractInput} hidden type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => { const f = e.target.files?.[0] || null; setContractFile(f); if (f && !contractTitle) setContractTitle(f.name.replace(/\.[^.]+$/, '')); }} />
                  </Button>
                  <Button size="small" variant="contained" disabled={busy || !contractFile} onClick={sendContract}>Send for signature</Button>
                </Box>
                <Typography variant="caption" color="text.secondary">A one-off file is signed on a certificate page at the end; a template puts their signature, initials and dates exactly where you placed them. They get a signed copy by email; you get one too. Templates with company boxes get a Sign off button once they've signed. {ob.wordSupported ? 'Word files are converted to PDF on the server.' : 'Word files: the server cannot convert them yet — in Word use File → Save As → PDF.'}</Typography>
              </>
            );
          })()}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Actions</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canSchedule && <Button variant="contained" disabled={busy} onClick={() => setScheduling((s) => !s)}>{scheduling ? 'Close scheduler' : a.interview?.scheduledFor ? 'Propose a different time' : 'Schedule interview'}</Button>}
            {a.assessment.completedAt && !['hired', 'rejected', 'withdrawn'].includes(a.status) && !a.interview?.scheduledFor && <Button disabled={busy} onClick={() => post('request-availability', {}, 'Emailed them to ask for their times')}>{a.interview?.availabilityRequestedAt ? 'Ask again for their times' : 'Ask for their times'}</Button>}
            {a.status !== 'hired' && <Button variant="outlined" color="success" disabled={busy} onClick={hire}>Mark hired</Button>}
            {a.status === 'hired' && <Button variant="outlined" color="warning" disabled={busy} onClick={unhire}>Undo hire</Button>}
            {['rejected', 'withdrawn'].includes(a.status) && <Button variant="contained" disabled={busy} onClick={reopen}>Reverse rejection</Button>}
            {!['rejected', 'hired'].includes(a.status) && <Button variant="outlined" color="error" disabled={busy} onClick={() => setRejecting((s) => !s)}>Not selected</Button>}
            {!['hired', 'rejected', 'withdrawn'].includes(a.status) && !a.assessment.completedAt && <Button disabled={busy} onClick={() => post('resend-assessment', {}, 'Assessment email sent')}>Resend assessment email</Button>}
          </Stack>
          {scheduling && (
            <Box sx={{ mt: 2 }}>
              <SlotPicker applicantId={id} value={when} onChange={setWhen} notify={notify} />
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">{when ? `Propose ${fmtCentral(when)} Central` : 'Pick a slot above'}</Typography>
                <Button variant="contained" disabled={busy || !when} onClick={schedule}>Email this time</Button>
                <Typography variant="caption" color="text.secondary">They get a "yes, that works" button and a link to pick another open slot.</Typography>
              </Box>
            </Box>
          )}
          {hiring && <HireDialog applicant={{ ...a, id }} notify={notify} onClose={() => setHiring(false)} onHired={async () => { setHiring(false); await load(); onChanged(); }} />}
          {rejecting && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" fullWidth label="Internal reason (optional, not emailed)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <Button variant="contained" color="error" disabled={busy} onClick={reject}>Send "not selected"</Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Notes</Typography>
          {(a.notes || []).length === 0 && <Typography variant="body2" color="text.secondary">No notes yet</Typography>}
          {(a.notes || []).map((n, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}><Typography component="span" variant="caption" color="text.secondary">{fmtDate(n.at)}{n.by ? ` · ${n.by}` : ''}: </Typography>{n.text}</Typography>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField size="small" fullWidth label="Add a note" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
            <Button variant="outlined" disabled={busy || !note.trim()} onClick={addNote}>Add</Button>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Emails sent</Typography>
          {(a.emails || []).length === 0 && <Typography variant="caption" color="text.secondary">None yet</Typography>}
          {(a.emails || []).map((e, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">{emailLabel(e.type)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{fmtEmailTime(e.at)}</Typography>
            </Box>
          ))}
        </>
      )}
    </Drawer>
  );
}

// ---- applicants table ----------------------------------------------------------------------

// Hiring area they applied for, falling back to their home city for older records
const areaOf = (r) => r.applyCity || [r.city, r.state].filter(Boolean).join(', ');

const STATUS_ORDER = ['applied', 'assessment_sent', 'assessment_in_progress', 'assessment_complete', 'interview_scheduled', 'hired', 'rejected', 'withdrawn'];
const text = (v) => String(v || '').toLowerCase();
const when = (v) => (v ? new Date(v).getTime() || 0 : 0);
// Column -> sortable value. Blank values always sink to the bottom whichever way it is sorted.
const SORT_VALUE = {
  name: (r) => text(r.name),
  phone: (r) => String(r.phone || '').replace(/\D/g, ''),
  email: (r) => text(r.email),
  area: (r) => text(areaOf(r)),
  city: (r) => text([r.city, r.state].filter(Boolean).join(', ')),
  applied: (r) => when(r.appliedAt),
  // Pipeline order, then by interview time within a status (so "Interview scheduled" lists by the clock)
  status: (r) => { const i = STATUS_ORDER.indexOf(r.status); return (i < 0 ? STATUS_ORDER.length : i) * 1e13 + when(r.interview?.scheduledFor); },
  assessment: (r) => (r.assessment?.modulesPassed || 0) * 1000 + (r.assessment?.videosUploaded || 0),
};
const SORT_COLUMNS = [['name', 'Name'], ['phone', 'Phone'], ['email', 'Email'], ['area', 'Area'], ['city', 'City'], ['applied', 'Applied'], ['status', 'Status'], ['assessment', 'Assessment']];

function ApplicantsCard({ jobs, notify, refreshKey, onSelect }) {
  const [status, setStatus] = useState('');
  const [section, setSection] = useState('active');
  const [jobId, setJobId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [sort, setSort] = useState({ key: '', dir: 'asc' });

  const areas = useMemo(() => [...new Set(rows.map(areaOf).filter(Boolean))].sort(), [rows]);
  // Per-section counts are only right when no status chip narrows the server query
  const sectionCounts = useMemo(() => rows.reduce((acc, r) => { acc[sectionOf(r)] = (acc[sectionOf(r)] || 0) + 1; return acc; }, {}), [rows]);
  const inSection = rows.filter((r) => sectionOf(r) === section);
  const needle = query.trim().toLowerCase();
  const needleDigits = needle.replace(/\D/g, '');
  const visible = inSection.filter((r) => {
    if (area && areaOf(r) !== area) return false;
    if (!needle) return true;
    const haystack = [r.name, r.email, r.city, r.state, r.applyCity, r.jobSlug, r.jobTitle, r.status, STATUS_LABEL[r.status]].filter(Boolean).join(' ').toLowerCase();
    if (haystack.includes(needle)) return true;
    return needleDigits.length >= 3 && String(r.phone || '').replace(/\D/g, '').includes(needleDigits);
  });
  // Filtering to "Interview scheduled" lists soonest first until a column is chosen
  const effectiveSort = useMemo(() => (sort.key || status !== 'interview_scheduled' ? sort : { key: 'status', dir: 'asc' }), [sort, status]);
  const sorted = useMemo(() => {
    const sort = effectiveSort;
    if (!sort.key) return visible;
    const val = SORT_VALUE[sort.key];
    const dir = sort.dir === 'desc' ? -1 : 1;
    return visible.map((r, i) => [r, i, val(r)]).sort((a, b) => {
      const ea = a[2] === '' || (sort.key === 'applied' && a[2] === 0);
      const eb = b[2] === '' || (sort.key === 'applied' && b[2] === 0);
      if (ea !== eb) return ea ? 1 : -1;
      if (a[2] < b[2]) return -dir;
      if (a[2] > b[2]) return dir;
      return a[1] - b[1];
    }).map((x) => x[0]);
  }, [visible, effectiveSort]);
  const clickSort = (key) => {
    setPage(0);
    setSort((cur) => (cur.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : { key: '', dir: 'asc' }));
  };
  const narrowed = !!(needle || area);
  const safePage = Math.min(page, Math.max(0, Math.ceil(visible.length / perPage) - 1));
  const shown = sorted.slice(safePage * perPage, safePage * perPage + perPage);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (jobId) params.set('jobId', jobId);
      const { data } = await axios.get(`${API}/api/careers-admin/applicants?${params}`, { headers: getAuthHeader() });
      setRows(data.applicants || []);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load applicants', 'error');
    } finally {
      setLoading(false);
    }
  }, [status, jobId, notify]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Applicants</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Posting</InputLabel>
          <Select label="Posting" value={jobId} onChange={(e) => setJobId(e.target.value)}>
            <MenuItem value="">All postings</MenuItem>
            {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.title}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={section}
        onChange={(e, next) => { if (next) { setSection(next); setStatus(''); setPage(0); } }}
        sx={{ mb: 2 }}
      >
        {SECTIONS.map(([key, label]) => (
          <ToggleButton key={key} value={key}>
            {label}{status === '' && rows.length > 0 ? ` (${sectionCounts[key] || 0})` : ''}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {SECTION_FILTERS[section].length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
          <Chip label="All" color={status === '' ? 'primary' : 'default'} onClick={() => { setStatus(''); setPage(0); }} />
          {SECTION_FILTERS[section].map((s) => <Chip key={s} label={STATUS_LABEL[s]} color={status === s ? 'primary' : 'default'} onClick={() => { setStatus(s); setPage(0); }} />)}
        </Stack>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          sx={{ minWidth: 280, flex: '1 1 280px', maxWidth: 480 }}
          label="Search applicants"
          placeholder="name, email, phone, city, state, area, posting, status"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(0); }}
          InputProps={{ endAdornment: query ? <Button size="small" onClick={() => setQuery('')}>Clear</Button> : null }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Location</InputLabel>
          <Select label="Location" value={area} onChange={(e) => { setArea(e.target.value); setPage(0); }}>
            <MenuItem value="">All locations</MenuItem>
            {areas.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">{narrowed ? `${visible.length} of ${inSection.length}` : `${inSection.length}`} applicant{inSection.length === 1 ? '' : 's'}</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            {SORT_COLUMNS.map(([key, label]) => (
              <TableCell key={key} sortDirection={effectiveSort.key === key ? effectiveSort.dir : false}>
                <TableSortLabel active={effectiveSort.key === key} direction={effectiveSort.key === key ? effectiveSort.dir : 'asc'} onClick={() => clickSort(key)}>{key === 'status' && status === 'interview_scheduled' ? 'Interview time' : label}</TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 && <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell></TableRow>}
          {!loading && inSection.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">{status ? 'No applicants with that status' : section === 'hired' ? 'Nobody hired yet' : section === 'closed' ? 'Nobody marked not selected or withdrawn' : 'No applicants in the pipeline'}</Typography></TableCell></TableRow>}
          {!loading && inSection.length > 0 && visible.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No applicants match{needle ? ` "${query.trim()}"` : ''}{area ? ` in ${area}` : ''}</Typography></TableCell></TableRow>}
          {shown.map((r) => (
            <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => onSelect(r.id)}>
              <TableCell>{r.name}{r.historyFlag ? <span title="Answered yes to a background or driving-history question" style={{ color: '#ed6c02', marginLeft: 6 }}>⚑</span> : null}{r.hasResume ? <Typography variant="caption" display="block" color="text.secondary">resume attached</Typography> : null}</TableCell>
              <TableCell>{r.phone}</TableCell>
              <TableCell>{r.email}</TableCell>
              <TableCell>{r.applyCity || '—'}</TableCell>
              <TableCell>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</TableCell>
              <TableCell>{fmtDay(r.appliedAt)}</TableCell>
              <TableCell>
                <StatusChip status={r.status} />
                {(() => { const s = !['hired', 'rejected', 'withdrawn'].includes(r.status) ? interviewSummary(r.interview) : (r.interview?.scheduledFor ? { text: `Interview ${fmtCentral(r.interview.scheduledFor)}` } : null); return s ? <Typography variant="caption" display="block" color="text.secondary">{s.text}</Typography> : null; })()}
                {r.interview?.phase === 'availability' && r.interview.availability?.length > 0 && (
                  <Typography variant="caption" display="block" sx={{ color: 'success.main' }}>
                    {r.interview.availability.slice(0, 3).map(fmtCentral).join(' · ')}{r.interview.availability.length > 3 ? ` · +${r.interview.availability.length - 3} more` : ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">modules {r.assessment.modulesPassed}/{r.assessment.moduleCount} · videos {r.assessment.videosUploaded}/{r.assessment.moduleCount}</Typography>
                <Typography variant="caption" color="text.secondary">{r.assessment.completedAt ? `done ${fmtDay(r.assessment.completedAt)}` : r.assessment.emailSentAt ? `invited ${fmtEmailTime(r.assessment.emailSentAt)}` : `invite due ${fmtDate(r.assessment.emailDueAt)}`}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {visible.length > PAGE_SIZES[0] && (
        <TablePagination component="div" count={visible.length} page={safePage} rowsPerPage={perPage} rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }} />
      )}
    </Paper>
  );
}

/** Applicants who finished before the calendar existed and were never asked for their times. */
function AvailabilityNudge({ notify, refreshKey, onChanged }) {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/careers-admin/availability-requests`, { headers: getAuthHeader() });
        setPending(data.pending || []);
      } catch (e) { /* the list is a convenience; the drawer still has the per-applicant button */ }
    })();
  }, [refreshKey]);
  if (!pending.length) return null;
  const send = async () => {
    if (!window.confirm(`Email ${pending.length} applicant${pending.length === 1 ? '' : 's'} asking which times they're free for a phone interview?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/careers-admin/availability-requests/send`, {}, { headers: getAuthHeader() });
      notify(`Asked ${data.sent} applicant${data.sent === 1 ? '' : 's'} for their times`, 'success');
      setPending([]);
      onChanged();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to send', 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Alert severity="info" sx={{ mb: 2 }} action={<Button size="small" variant="contained" disabled={busy} onClick={send}>Email them</Button>}>
      {pending.length} applicant{pending.length === 1 ? '' : 's'} finished the assessment and {pending.length === 1 ? 'has' : 'have'} never been asked for interview times: {pending.map((p) => p.name).join(', ')}.
    </Alert>
  );
}

// ---- tab ----------------------------------------------------------------------------------

/**
 * Hiring pipeline. `view` picks the Owner Console section: 'postings' (job
 * postings + pipeline settings), 'applicants' or 'reviewer' (assessment
 * reviewer link).
 */
const VIEW_TITLE = {
  postings: ['Job postings', 'Driver postings on the hiring site, whiteknightroadsideservices.com.'],
  applicants: ['Applicants', 'Pipeline settings (including the quiz pass mark), applications, Technician Skills Assessment results and phone interviews.'],
  reviewer: ['Assessment reviewer link', 'A private link for someone to proof-read the Technician Skills Assessment.']
};

function Hiring({ view = 'postings' }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [snack, setSnack] = useState(null);
  const notify = useCallback((message, severity = 'info') => setSnack({ message, severity }), []);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/careers-admin/jobs`, { headers: getAuthHeader() });
      setJobs(data.jobs || []);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load postings', 'error');
    }
  }, [notify]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/careers-admin/settings`, { headers: getAuthHeader() });
        setSettings(data.settings);
      } catch (err) {
        notify(err.response?.data?.message || 'Failed to load hiring settings', 'error');
      } finally {
        setLoading(false);
      }
      loadJobs();
    })();
  }, [loadJobs, notify]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/careers-admin/settings`, {
        assessmentDelayMinutes: Number(settings.assessmentDelayMinutes),
        passScorePercent: Number(settings.passScorePercent),
        videosRequired: settings.videosRequired === true,
        notifyEmail: settings.notifyEmail,
        interviewEmailTemplate: settings.interviewEmailTemplate || '',
        interviewDays: settings.interviewDays,
        interviewStartHour: Number(settings.interviewStartHour),
        interviewEndHour: Number(settings.interviewEndHour),
        interviewSlotMinutes: Number(settings.interviewSlotMinutes),
        interviewWeeksAhead: Number(settings.interviewWeeksAhead),
        interviewRemindersEnabled: settings.interviewRemindersEnabled !== false,
        interviewReminderLeadMinutes: Number(settings.interviewReminderLeadMinutes),
        interviewReminderRepeatMinutes: Number(settings.interviewReminderRepeatMinutes),
        interviewReminderSnoozeMinutes: Number(settings.interviewReminderSnoozeMinutes),
        interviewReminderDurationMinutes: Number(settings.interviewReminderDurationMinutes)
      }, { headers: getAuthHeader() });
      setSettings(data.settings);
      notify('Settings saved', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changed = () => { loadJobs(); setRefreshKey((k) => k + 1); };

  if (loading && !settings) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  const [title, blurb] = VIEW_TITLE[view] || VIEW_TITLE.postings;

  return (
    <Box>
      <Box sx={{ mb: 2, mt: 2 }}>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{blurb}</Typography>
      </Box>
      {view === 'postings' && (
        <PostingsCard jobs={jobs} onChanged={changed} notify={notify} siteUrl={settings?.careersSiteUrl || DEFAULT_SITE_URL} />
      )}
      {view === 'reviewer' && <ReviewerCard notify={notify} />}
      {view === 'applicants' && (
        <>
          {settings && <SettingsCard settings={settings} setSettings={setSettings} onSave={saveSettings} saving={saving} />}
          <AvailabilityNudge notify={notify} refreshKey={refreshKey} onChanged={changed} />
          <ApplicantsCard jobs={jobs} notify={notify} refreshKey={refreshKey} onSelect={setSelected} />
          <ApplicantDrawer id={selected} onClose={() => setSelected(null)} notify={notify} onChanged={changed} />
        </>
      )}
      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default Hiring;
